import { Component } from '@theme/component';

/**
 * Counts down to a fixed moment and switches to its expired state when the moment passes.
 * The end time comes from `data-end` (Unix seconds). `data-expired-behavior` is either
 * `hide` (the element hides itself) or `message` (the `expired` ref replaces `running`).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [running] - Wrapper shown while the countdown runs.
 * @property {HTMLElement} [expired] - Message shown after the end time.
 * @property {HTMLElement} [daysUnit] - Days unit wrapper, hidden when no full days remain.
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 *
 * @extends {Component<Refs>}
 */
class CountdownTimer extends Component {
  /** @type {number | undefined} */
  #timeout = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#stop();
    this.#tick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  /** @returns {number} End time in milliseconds, or NaN when unset. */
  get end() {
    return Number(this.dataset.end) * 1000;
  }

  #stop() {
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
  }

  #tick = () => {
    const remaining = this.end - Date.now();
    if (Number.isNaN(remaining)) return;

    if (remaining <= 0) {
      this.#expire();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const { refs } = this;

    if (refs.daysUnit) refs.daysUnit.hidden = days === 0;
    if (refs.days) refs.days.textContent = String(days);
    if (refs.hours) refs.hours.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
    if (refs.minutes) refs.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
    if (refs.seconds) refs.seconds.textContent = pad(totalSeconds % 60);

    // Align the next update with the start of the next second.
    this.#timeout = setTimeout(this.#tick, remaining % 1000 || 1000);
  };

  #expire() {
    this.#stop();

    if (this.dataset.expiredBehavior === 'message' && this.refs.expired) {
      if (this.refs.running) this.refs.running.hidden = true;
      this.refs.expired.hidden = false;
      return;
    }

    this.hidden = true;
  }
}

/**
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return String(value).padStart(2, '0');
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
